import type { Asset } from '@mado/schema';
import { pickVariant, srcsetFor } from '@mado/schema/assets';

/** Codec support probe, memoised — called on every asset mount. */
const support = new Map<string, boolean>();
export function supports(codec: 'avif' | 'webp'): boolean {
  let s = support.get(codec);
  if (s === undefined) {
    const c = document.createElement('canvas');
    c.width = c.height = 1;
    s = c.toDataURL(`image/${codec}`).startsWith(`data:image/${codec}`);
    support.set(codec, s);
  }
  return s;
}

/**
 * Variant URLs in the document are root-relative (`/a/<key>` — see
 * `@mado/schema/assets` `assetPath`), never an absolute CDN host, because a
 * published document is immutable for the life of the product and cannot bake
 * in a host that might change.
 *
 * Resolving them is the player's job, and where "against what" differs by how
 * the document was loaded:
 *   - iframe embed (`/e/:slug`): the frame's own origin already serves `/a/*`,
 *     so a root-relative URL resolves correctly with no help — `base` is null.
 *   - `data-inline` (the web component mounted straight into a customer's DOM):
 *     the image tags live in the HOST page, so an unresolved `/a/...` would
 *     resolve against the host's origin instead of ours. `base` is the origin
 *     the document was fetched from (or an explicit `asset-base` attribute for
 *     an inline JSON document with no `src` to infer it from).
 *
 * Anything that is not root-relative (already absolute, or a relative path
 * from `tools/authoring`'s local preview) passes through untouched.
 */
export function resolveAssetUrl(url: string, base: string | null | undefined): string {
  if (!base || !url.startsWith('/')) return url;
  return base.replace(/\/+$/, '') + url;
}

function resolveSrcset(
  srcset: string | undefined,
  base: string | null | undefined,
): string | undefined {
  if (!srcset || !base) return srcset;
  return srcset
    .split(', ')
    .map((entry) => {
      const i = entry.lastIndexOf(' ');
      const url = i < 0 ? entry : entry.slice(0, i);
      const width = i < 0 ? '' : entry.slice(i);
      return `${resolveAssetUrl(url, base)}${width}`;
    })
    .join(', ');
}

/**
 * The variant URL for a plain <img> (the chrome's logo). Not for step
 * pictures: `supports` asks whether the canvas can ENCODE a codec, and
 * Chromium and Safari decode AVIF but cannot encode it — prefetch built on
 * this warmed WebP (Chromium) or JPEG (Safari) while the <picture> loaded
 * AVIF. Prefetch builds the same <picture> the stage does instead.
 */
export function bestUrl(asset: Asset, cssWidth: number, dpr: number, base?: string | null): string {
  const v = pickVariant(asset, cssWidth, dpr);
  const url = v.avif && supports('avif') ? v.avif : v.webp && supports('webp') ? v.webp : v.jpg;
  return resolveAssetUrl(url, base);
}

/**
 * Build a <picture> at the asset's natural size. Natural sizing matters: the
 * stage scales it with a single `transform`, so the browser never relayouts on
 * step change or resize — which is what keeps CLS at 0 and the pan at 60fps.
 */
export function buildPicture(asset: Asset, sizesPx: number, base?: string | null): HTMLElement {
  const pic = document.createElement('picture');

  for (const codec of ['avif', 'webp'] as const) {
    const ss = resolveSrcset(srcsetFor(asset, codec), base);
    if (!ss) continue;
    const source = document.createElement('source');
    source.type = `image/${codec}`;
    source.srcset = ss;
    source.sizes = `${Math.round(sizesPx)}px`;
    pic.appendChild(source);
  }

  // Into the <picture> before any source attribute is set: an <img> given a
  // `src` while still on its own starts fetching that file at once (WebKit
  // does), a download the <picture> then throws away for its AVIF/WebP.
  const img = document.createElement('img');
  pic.appendChild(img);
  const jpgSet = resolveSrcset(srcsetFor(asset, 'jpg'), base);
  if (jpgSet) {
    img.sizes = `${Math.round(sizesPx)}px`;
    img.srcset = jpgSet;
  }
  img.src = resolveAssetUrl(pickVariant(asset, sizesPx, 1).jpg, base);
  img.width = asset.w;
  img.height = asset.h;
  // Inline, so no page rule (`img { max-width: 100% }`, `img { width: 100% }`)
  // can resize the picture the camera's transform is computed for (styles.ts).
  img.style.width = `${asset.w}px`;
  img.style.height = `${asset.h}px`;
  img.style.maxWidth = 'none';
  img.style.maxHeight = 'none';
  img.decoding = 'async';
  img.alt = '';
  img.setAttribute('draggable', 'false');
  if (asset.placeholder) {
    // Painted instantly under the real frame; removed once decoded.
    img.style.background = `var(--mado-bg) url(${asset.placeholder}) center/cover no-repeat`;
  }
  return pic;
}
